/**
 * Routing for AGS recommendation cards (Next Best Steps, Current Focus steps).
 *
 * The feed marks recommendation-backed cards with `action` (the backend
 * RecommendationType). Platform actions have no dialogue behind them — sending
 * them to /explore/dialogue/{id} 404s — so they route to their real surface.
 * Dialogue-backed actions (GUIDED_DIALOGUE, REFLECTION, …) return null here and
 * fall through to the dialogue detail screen.
 */
const ACTION_ROUTES = {
  // The member's personal model lives on the production Periodic Table page.
  EXPLORE_PERSONAL_MODEL: "/elements",
  EXPLORE_NEW_ELEMENTS: "/elements",
  TALK_ABOUT_ELEMENT: "/elements",
  // Foundational discoveries list.
  COMPLETE_FOUNDATIONAL_JOURNEY: "/explore/self-knowledge",
  CONTINUE_DISCOVERY: "/explore/self-knowledge",
  ASSESSMENT: "/explore/self-knowledge",
  // Direction surfaces. "Revisit your Mission" opens Mission Evolution
  // (entry_type MISSION_EVOLUTION); the card carries no recommendation_id.
  UPDATE_MISSION: "/journey/mission/evolve?entry_source=AGS_RECOMMENDATION",
  REVIEW_PRIORITIES: "/journey/mission",
  // Conversational actions happen in chat with Aris.
  DAILY_CHECK_IN: "/dashboard",
  PRAYER: "/dashboard",
  BREATHWORK: "/dashboard",
  REST: "/dashboard",
  CONVERSATION_PREPARATION: "/dashboard",
  JOURNAL_ENTRY: "/dashboard",
  PROFESSIONAL_SUPPORT: "/dashboard",
};

/** Route for a platform action, or null when the card is a real dialogue. */
export function routeForAction(action) {
  return (action && ACTION_ROUTES[action]) || null;
}

/**
 * Can this card reach anything when tapped? Either it is a platform action with
 * its own surface, or it carries the `dialogue_id` the detail page needs.
 *
 * `dialogue_id` is optional on the UniversalDialogueCard contract and aris-ai
 * leaves it unset when a recommendation's framework doesn't resolve, so a
 * dialogue-backed recommendation can arrive with no destination at all. Rails
 * filter on this rather than render a card that goes nowhere.
 */
export function isOpenable(card) {
  return Boolean(routeForAction(card?.action) || card?.dialogue_id);
}

/**
 * Open a feed card: platform actions go to their surface, dialogue cards to the
 * dialogue detail screen.
 *
 * Anything else does nothing. Tapping a recommendation must reach its detail
 * page — the commitment screen — and never silently start a conversation the
 * member didn't ask for, so there is deliberately no fallback destination here:
 * the old `/dashboard` one dropped the member into chat with Aris. Rendered
 * cards are pre-filtered by `isOpenable`, so this branch means a caller passed
 * a card that was never openable; the warning surfaces that without hijacking
 * the tap.
 */
export function openCard(card, navigate) {
  const actionRoute = routeForAction(card?.action);
  if (actionRoute) return navigate(actionRoute);
  if (card?.dialogue_id) return navigate(`/explore/dialogue/${card.dialogue_id}`);
  console.warn("Explore: card has no destination, ignoring open", card?.title);
  return undefined;
}

export default openCard;
